import React from 'react'
import { ScrollView, Text, View } from 'react-native'
import { useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'

import { GET_CURRENT_VIP_RECORD, GET_VIP_SETTING } from '@/constants/graphql/queries'
import { formatLocalFiatAmount, formatMarketNumber, marketPresentation } from '@/config/marketPresentation'
import { cn } from '@/utils/cn'
import i18n from '@/utils/i18n'
import { numberWithCommas } from '@/utils/numbers'
import {
  BottomLeftTriangle,
  BottomRightTriangle,
  BottomTriangle,
  Check,
  CheckCircle,
  Info,
  TopLeftTriangle
} from '@/components/icon'
import { Stack } from '@/components/layout'
import NavigationBar from '@/components/NavigationBar'
import VipCard from '@/components/VipCard'

export default function VipScreen() {
  const { t } = useTranslation()
  const { data } = useQuery(GET_CURRENT_VIP_RECORD, { pollInterval: 60 * 1000 })
  const { data: vipSetting } = useQuery(GET_VIP_SETTING)
  const level = data?.getCurrentVipRecord.level
  const progressBarStatus = getProgressBarStatus(level, vipSetting?.getVipSetting.data, data?.getCurrentVipRecord)
  return (
    <>
      <Stack.Screen
        options={{
          header: () => <NavigationBar hasBottomBorder={false}>{t('vip:title')}</NavigationBar>
        }}
      />
      <ScrollView className={'flex-1 bg-background-primary px-4'} contentContainerStyle={{ paddingBottom: 16 }}>
        {/* Banner */}
        <View className={'border-b border-border-10 pb-4 pt-2'}>
          <VipCard hasLabel={true} />
        </View>
        {/* Condition */}
        <View className={'gap-4 border-b border-border-10 py-4'}>
          <Text className={'text-base font-medium text-text-primary'}>{t('vip:levelChangeConditions')}</Text>
          <IndicatorGroup level={level} progressBarStatus={progressBarStatus} />
          <ProgressBar status={progressBarStatus} />
          {/* Progress Card */}
          <View className={'flex-row'}>
            {level === 0 && (
              <ProgressCard
                action={'upgrade'}
                isActive={true}
                data={data?.getCurrentVipRecord}
                settings={vipSetting?.getVipSetting.data}
                level={level + 1}
                collapsible={true}
              />
            )}
            {level === 10 && (
              <ProgressCard
                action={'maintain'}
                isActive={true}
                data={data?.getCurrentVipRecord}
                settings={vipSetting?.getVipSetting.data}
                level={10}
                collapsible={true}
              />
            )}
            {level !== 0 && level !== 10 && (
              <>
                <ProgressCard
                  action={'maintain'}
                  isActive={progressBarStatus === 'start'}
                  data={data?.getCurrentVipRecord}
                  settings={vipSetting?.getVipSetting.data}
                  level={level}
                  collapsible={false}
                />
                <ProgressCard
                  action={'upgrade'}
                  isActive={true}
                  data={data?.getCurrentVipRecord}
                  settings={vipSetting?.getVipSetting.data}
                  level={level + 1}
                  collapsible={false}
                />
              </>
            )}
          </View>
          <View className={'flex-row items-start gap-1 pl-2'}>
            <Info className={'size-4 min-w-4 text-text-secondary'} />
            <Text className={'text-xs font-medium text-text-secondary'}>
              {t('vip:levelChangeHint')}
            </Text>
          </View>
        </View>
        {/* Table */}
        <View className={'gap-4 py-4'}>
          <Text className={'text-base font-medium text-text-primary'}>{t('vip:compareBenefits')}</Text>
        </View>

        <ScrollView horizontal showsHorizontalScrollIndicator={false} className={'rounded border border-border-10'}>
          <View className={'min-w-[400px]'}>
            <View className={'flex-row bg-border-10'}>
              <TableCell width={80} header>
                {t('vip:vipLevel')}
              </TableCell>
              <TableCell width={140} header>
                {t('vip:sellOrderLevelCondition')}
              </TableCell>
              <TableCell width={40} header>
                {t('vip:or')}
              </TableCell>
              <TableCell width={140} header>
                {t('vip:exchangeLevelCondition', { token: marketPresentation.platformToken.displayName })}
              </TableCell>
            </View>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((index) => (
              <View className={'flex-row'} key={index}>
                <TableCell width={80}>{t('vip:levelLabel', { level: index })}</TableCell>
                <TableCell width={140}>
                  {!vipSetting?.getVipSetting.data?.[index]?.sellOrderCount.toString() ||
                  !vipSetting?.getVipSetting.data?.[index]?.sellOrderAmount
                      ? '−'
                    : displaySellCondition(
                        vipSetting.getVipSetting.data[index].sellOrderCount,
                        vipSetting.getVipSetting.data[index].sellOrderAmount
                      )}
                </TableCell>
                <TableCell width={40}>{t('vip:or')}</TableCell>
                <TableCell width={140}>
                  {vipSetting?.getVipSetting.data?.[index]?.exchangeYbbAmount
                    ? formatMarketNumber(vipSetting.getVipSetting.data[index].exchangeYbbAmount, 'YBB')
                    : '−'}
                </TableCell>
              </View>
            ))}
          </View>
        </ScrollView>
      </ScrollView>
    </>
  )
}

function TableCell({
  children,
  width,
  header = false
}: {
  children: React.ReactNode
  width: number
  header?: boolean
}) {
  return (
    <View className={cn('border-b border-r border-border-10 px-3 py-2.5', header && 'bg-border-10')} style={{ width }}>
      <Text className={cn('text-xs font-medium', header ? 'text-text-tertiary' : 'text-text-primary')}>{children}</Text>
    </View>
  )
}

function IndicatorGroup({ level, progressBarStatus }: { level?: number; progressBarStatus: string }) {
  const { t } = useTranslation()
  if (level === 0) {
    return (
      <View className={'flex-row justify-between'}>
        <View className={'flex-1'} />
        {progressBarStatus === 'middle' && (
          <ProgressBarIndicator text={t('vip:maintainNext', { level: 0 })} status={'active'} position={'center'} />
        )}
        <View className={'flex-1'}>
          <ProgressBarIndicator
            text={t('vip:promote', { level: 1 })}
            status={progressBarStatus === 'middle' ? 'target' : 'active'}
            position={'right'}
          />
        </View>
      </View>
    )
  }
  if (level === 10) {
    return (
      <View className={'flex-row justify-between'}>
        {progressBarStatus === 'start' && (
          <View className={'flex-1'}>
            <ProgressBarIndicator text={t('vip:demoteNext', { level: 9 })} status={'active'} position={'left'} />
          </View>
        )}
        <View className={'flex-1'}>
          <ProgressBarIndicator
            text={progressBarStatus === 'start' ? t('vip:maintain', { level: 10 }) : t('vip:maintainNext', { level: 10 })}
            status={progressBarStatus === 'end' ? 'active' : 'target'}
            position={'right'}
          />
        </View>
      </View>
    )
  }
  if (level === undefined) {
    return (
      <View className={'flex-row justify-between'}>
        <View className={'flex-1'}>
          <ProgressBarIndicator text={t('vip:demoteNext', { level: '−' })} status={'inactive'} position={'left'} />
        </View>
        <ProgressBarIndicator text={t('vip:maintain', { level: '−' })} status={'inactive'} position={'center'} />
        <View className={'flex-1'}>
          <ProgressBarIndicator text={t('vip:promote', { level: '−' })} status={'inactive'} position={'right'} />
        </View>
      </View>
    )
  }
  return (
    <View className={'flex-row justify-between'}>
      <View className={'flex-1'}>
        {progressBarStatus === 'start' && (
          <ProgressBarIndicator text={t('vip:demoteNext', { level: level - 1 })} status={'active'} position={'left'} />
        )}
      </View>
      {(progressBarStatus === 'start' || progressBarStatus === 'middle') && (
        <ProgressBarIndicator
          text={t('vip:maintain', { level })}
          status={progressBarStatus === 'start' ? 'target' : 'active'}
          position={'center'}
        />
      )}
      <View className={'flex-1'}>
        <ProgressBarIndicator
          text={t('vip:promote', { level: level + 1 })}
          status={progressBarStatus === 'start' || progressBarStatus === 'middle' ? 'target' : 'active'}
          position={'right'}
        />
      </View>
    </View>
  )
}

const backgroundStyle = {
  active: 'bg-info-subtle',
  target: 'bg-fill-subtle',
  inactive: 'bg-fill-subtle'
}
const iconStyle = {
  active: 'text-info-subtle',
  target: 'text-fill-subtle',
  inactive: 'text-fill-subtle'
}
const textStyle = {
  active: 'text-info',
  target: 'text-text-secondary',
  inactive: 'text-text-secondary opacity-30'
}

function ProgressBarIndicator({
  text,
  status,
  position
}: {
  text: string
  status: 'active' | 'target' | 'inactive'
  position: 'left' | 'center' | 'right'
}) {
  const { t } = useTranslation()
  return (
    <View
      className={cn(
        'flex w-fit flex-row gap-0.5 px-2 py-1',
        backgroundStyle[status],
        position === 'left' && 'rounded-t-lg rounded-br-lg',
        position === 'center' && 'rounded-lg',
        position === 'right' && 'ml-auto rounded-t-lg rounded-bl-lg'
      )}
    >
      {status === 'active' && <CheckCircle className={'size-4 text-info'} />}
      <Text className={cn('text-xs font-medium', textStyle[status])}>{text}</Text>
      {position === 'left' && (
        <BottomRightTriangle className={cn('absolute bottom-[-8px] left-0', iconStyle[status])} />
      )}
      {position === 'center' && (
        <BottomTriangle className={cn('absolute inset-x-0 bottom-[-8px] mx-auto', iconStyle[status])} />
      )}
      {position === 'right' && (
        <BottomLeftTriangle className={cn('absolute bottom-[-8px] right-0', iconStyle[status])} />
      )}
    </View>
  )
}

function ProgressBar({ status }: { status: 'none' | 'start' | 'middle' | 'end' }) {
  if (status === 'end') {
    return (
      <View className={'h-2 overflow-hidden rounded-full'}>
        <View className={cn('h-full border-r-2 border-info-hover bg-info')} />
        <View className={'absolute inset-y-0 left-[calc(50%-2px)] w-0.5 bg-info-hover'} />
      </View>
    )
  }

  const indicatorStyle = {
    none: 'w-0',
    start: 'w-3/12',
    middle: 'w-9/12'
  }

  const dividerStyle = {
    none: 'bg-fill-subtle-hover',
    start: 'bg-fill-subtle-hover',
    middle: 'bg-info-hover'
  }

  return (
    <View className={'h-2 rounded-full bg-fill-subtle'}>
      <View className={cn('absolute inset-0 rounded-full bg-info', indicatorStyle[status])} />
      <View className={cn('absolute inset-y-0 left-[calc(50%-2px)] w-0.5', dividerStyle[status])} />
    </View>
  )
}

function ProgressCard({
  action,
  isActive,
  data,
  settings,
  level,
  collapsible
}: {
  action: 'maintain' | 'upgrade'
  isActive: boolean
  data: Record<string, any>
  settings: any[]
  level: number
  collapsible: boolean
}) {
  const { t } = useTranslation()

  return (
    <View className={'ml-2 flex-1 gap-2 rounded-b-lg rounded-tl-lg bg-fill-subtle p-3'}>
      <Text
        className={cn('text-sm font-medium text-text-primary', !isActive && 'opacity-30')}
      >{t('vip:conditionA', { action: t(action === 'maintain' ? 'vip:maintainLevel' : 'vip:upgrade') })}</Text>
      <View
        className={cn(
          'gap-2',
          !collapsible && 'min-h-[96px]',
          collapsible &&
            Number(settings?.[level]?.sellOrderCount) === 0 &&
            Number(settings?.[level]?.sellOrderAmount) === 0 &&
            'my-[-4px]'
        )}
      >
        {Number(settings?.[level]?.sellOrderCount) !== 0 && (
          <CardDetail
            title={t(isActive ? 'vip:currentPeriodSellOrderCount' : 'vip:currentPeriodSellOrderCountTarget')}
            hasCheck={Number(data?.sellOrderCount) >= Number(settings?.[level]?.sellOrderCount)}
            current={
              !isActive ? undefined : data?.sellOrderCount.toString() ? numberWithCommas(data.sellOrderCount) : '−'
            }
            target={
              settings?.[level]?.sellOrderCount.toString() ? numberWithCommas(settings[level]?.sellOrderCount) : '−'
            }
            isActive={isActive}
          />
        )}
        {Number(settings?.[level]?.sellOrderAmount) !== 0 && (
          <CardDetail
            title={t(isActive ? 'vip:currentPeriodSellOrderAmount' : 'vip:currentPeriodSellOrderAmountTarget', {
              token: marketPresentation.platformToken.displayName
            })}
            hasCheck={Number(data?.sellOrderAmount) >= Number(settings?.[level]?.sellOrderAmount)}
            current={
              !isActive
                ? undefined
                : data?.sellOrderAmount
                  ? numberWithCommas(Math.floor(Number(data.sellOrderAmount)))
                  : '−'
            }
            target={
              settings?.[level]?.sellOrderAmount
                ? numberWithCommas(Math.floor(Number(settings[level]?.sellOrderAmount)))
                : '−'
            }
            isActive={isActive}
          />
        )}
      </View>
      <View className={cn('h-px bg-border-20', !isActive && 'opacity-30')} />
      <Text
        className={cn('text-sm font-medium text-text-primary', !isActive && 'opacity-30')}
      >{t('vip:conditionB', { action: t(action === 'maintain' ? 'vip:maintainLevel' : 'vip:upgrade') })}</Text>
      {Number(settings?.[level]?.exchangeYbbAmount) !== 0 && (
        <CardDetail
          title={t(isActive ? 'vip:currentPeriodExchangeAmount' : 'vip:currentPeriodExchangeAmountTarget', {
            token: marketPresentation.platformToken.displayName
          })}
          hasCheck={Number(data?.exchangeYbbAmount) >= Number(settings?.[level]?.exchangeYbbAmount)}
          current={
            !isActive
              ? undefined
              : data?.exchangeYbbAmount
                ? numberWithCommas(Math.floor(Number(data.exchangeYbbAmount)))
                : '−'
          }
          target={
            settings?.[level]?.exchangeYbbAmount
              ? numberWithCommas(Math.floor(Number(settings[level]?.exchangeYbbAmount)))
              : '−'
          }
          isActive={isActive}
        />
      )}
      <TopLeftTriangle className={cn('absolute right-0 top-[-8px] text-fill-subtle')} />
    </View>
  )
}

function CardDetail({
  current,
  target,
  title,
  hasCheck,
  isActive
}: {
  current?: string
  target: string
  title: string
  hasCheck: boolean
  isActive: boolean
}) {
  return (
    <View className={'gap-1'}>
      <Text className={cn('text-xs font-medium text-text-secondary', !isActive && 'opacity-30')}>{title}</Text>
      <View className={cn('h-6 flex-row', !current ? 'pb-[3px] pt-[5px]' : 'items-baseline')}>
        {current && (
          <Text className={cn('font-roboto-medium text-base text-info', !isActive && 'opacity-30')}>{current}</Text>
        )}
        <Text className={cn('font-roboto-medium text-xs text-text-secondary', !isActive && 'opacity-30')}>
          {`${current ? '/' : ''}${target === 'NaN' ? '−' : target}`}
        </Text>
        {hasCheck && (
          <Check className={cn('relative left-0.5 size-4 text-info', !isActive && 'opacity-30', current && 'top-1')} />
        )}
      </View>
    </View>
  )
}

function getProgressBarStatus(level: number, settings?: any[], data?: Record<string, any>) {
  if (!settings || !data) {
    return 'none'
  }
  if (level === 0) {
    return meetConditionA(level + 1, settings, data) || meetConditionB(level + 1, settings, data) ? 'end' : 'middle'
  }
  if (level === 10) {
    return meetConditionA(level, settings, data) || meetConditionB(level, settings, data) ? 'end' : 'start'
  }
  if (meetConditionA(level + 1, settings, data) || meetConditionB(level + 1, settings, data)) {
    return 'end'
  }
  if (meetConditionA(level, settings, data) || meetConditionB(level, settings, data)) {
    return 'middle'
  } else {
    return 'start'
  }
}

function meetConditionA(level: number, settings: any[], data: Record<string, any>) {
  return (
    data.sellOrderCount >= settings[level].sellOrderCount &&
    Number(data.sellOrderAmount) >= Number(settings[level].sellOrderAmount)
  )
}

function meetConditionB(level: number, settings: any[], data: Record<string, any>) {
  return Number(data.exchangeYbbAmount) >= Number(settings[level].exchangeYbbAmount)
}

function displaySellCondition(sellOrderCount: number, sellOrderAmount: string): string | null {
  if (sellOrderCount === 0 && Number(sellOrderAmount) === 0) {
    return '−'
  }
  if (sellOrderCount !== 0 && Number(sellOrderAmount) === 0) {
    return i18n.t('vip:countOnly', { count: sellOrderCount })
  }
  if (sellOrderCount === 0 && Number(sellOrderAmount) !== 0) {
    return i18n.t('vip:amountOnly', { amount: formatLocalFiatAmount(Math.floor(Number(sellOrderAmount))) })
  }
  if (sellOrderCount !== 0 && Number(sellOrderAmount) !== 0) {
    return i18n.t('vip:countAndAmount', {
      count: sellOrderCount,
      amount: formatMarketNumber(Math.floor(Number(sellOrderAmount)), 'YBB'),
      token: marketPresentation.platformToken.displayName
    })
  }
  return null
}
